// src/components/admin/UsuarioDetalle.js

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import HistorialFichasAdmin from './HistorialFichasAdmin';
import { doc, getDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../../firebase/config';
import {
  FaArrowLeft,
  FaHome,
  FaSignOutAlt,
  FaUser,
  FaWeight,
  FaHeartbeat,
  FaUtensils,
  FaRunning,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaWhatsapp,
  FaUserCheck,
  FaUserSlash
} from 'react-icons/fa';

const APP_PRIMARY = '#b03060';

export default function UsuarioDetalle() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const montadoRef = useRef(true);

  const [usuario, setUsuario] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [mensajeExito, setMensajeExito] = useState('');
  const [procesandoAccion, setProcesandoAccion] = useState(false);
  const [mostrarModalBaja, setMostrarModalBaja] = useState(false);

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);

  // 1. Verificación defensiva de permisos de administración
  const fetchUsuario = useCallback(async () => {
    if (!uid || typeof uid !== 'string' || uid.trim().length === 0) {
      if (montadoRef.current) {
        setError('Identificador de usuario inválido.');
        setCargando(false);
      }
      return;
    }

    try {
      if (montadoRef.current) setCargando(true);
      const uidLimpio = uid.trim();
      const userRef = doc(db, 'usuarios', uidLimpio);
      const snap = await getDoc(userRef);

      if (!montadoRef.current) return;

      if (!snap.exists()) {
        setError('El usuario solicitado no existe en la base de datos.');
      } else {
        setUsuario(snap.data());
        setError('');
      }
    } catch {
      if (montadoRef.current) {
        setError('No fue posible cargar la ficha del alumno. Comprueba las directivas de seguridad.');
      }
    } finally {
      if (montadoRef.current) setCargando(false);
    }
  }, [uid]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      try {
        const adminDoc = await getDoc(doc(db, 'usuarios', user.uid));
        if (!adminDoc.exists()) {
          navigate('/login', { replace: true });
          return;
        }

        const data = adminDoc.data();
        const esAdmin = 
          data.isAdmin === true || 
          data.rol === 'admin' || 
          user.email === 'marianafitness.app@gmail.com';

        if (!esAdmin) {
          navigate('/home', { replace: true });
          return;
        }

        fetchUsuario();
      } catch {
        navigate('/login', { replace: true });
      }
    });

    return () => unsubscribe();
  }, [navigate, fetchUsuario]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login', { replace: true });
    } catch {
      navigate('/login', { replace: true });
    }
  };

  // ===============================
  // ACCIONES ADMINISTRATIVAS ATÓMICAS
  // ===============================
  const activarUsuario = async () => {
    if (!usuario || procesandoAccion || !uid) return;
    setProcesandoAccion(true);
    setError('');
    setMensajeExito('');

    try {
      const uidLimpio = uid.trim();
      const ref = doc(db, 'usuarios', uidLimpio);
      const hoy = new Date();
      let inicio = hoy;

      if (usuario.planEnd) {
        const fechaFin = usuario.planEnd.toDate ? usuario.planEnd.toDate() : new Date(usuario.planEnd);
        if (fechaFin > hoy) inicio = fechaFin;
      }

      const planRaw = String(usuario.plan || usuario.planSeleccionado || usuario.planNombreReportado || '').toLowerCase();
      let diasDuracion = 30;

      if (planRaw.includes('trial') || planRaw.includes('7') || planRaw.includes('seman')) {
        diasDuracion = 7;
      } else if (planRaw.includes('semestr')) {
        diasDuracion = 180;
      } else if (planRaw.includes('anual') || planRaw.includes('ano') || planRaw.includes('año')) {
        diasDuracion = 365;
      }

      const fin = new Date(inicio);
      fin.setDate(inicio.getDate() + diasDuracion);

      const esTrial = diasDuracion === 7;

      await updateDoc(ref, {
        subscriptionStatus: 'active',
        estado: 'active',
        eliminado: false,
        isDeleted: false,
        ...(esTrial ? { haUsadoTrial: true, trialUtilizado: true } : {}),
        plan: usuario.plan || (esTrial ? 'trial' : 'mensual'),
        planStart: Timestamp.fromDate(inicio),
        planEnd: Timestamp.fromDate(fin),
        activadoPorAdminEn: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      if (montadoRef.current) {
        setMensajeExito(`Acceso activado correctamente por ${diasDuracion} días.`);
        setTimeout(() => setMensajeExito(''), 4000);
      }

      await fetchUsuario();
    } catch {
      if (montadoRef.current) {
        setError('Error al actualizar el estado de suscripción del alumno.');
      }
    } finally {
      if (montadoRef.current) setProcesandoAccion(false);
    }
  };

  const ejecutarDarDeBaja = async () => {
    if (procesandoAccion || !uid) return;
    setProcesandoAccion(true);
    setError('');
    setMensajeExito('');

    try {
      const uidLimpio = uid.trim();
      const ref = doc(db, 'usuarios', uidLimpio);
      await updateDoc(ref, {
        eliminado: true,
        isDeleted: true,
        status: 'deleted',
        subscriptionStatus: 'inactivo',
        estado: 'inactivo',
        fechaBaja: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      setMostrarModalBaja(false);
      if (montadoRef.current) {
        setMensajeExito('El alumno ha sido dado de baja por inactividad.');
        setTimeout(() => setMensajeExito(''), 4000);
      }
      await fetchUsuario();
    } catch {
      if (montadoRef.current) {
        setError('No fue posible dar de baja al usuario.');
      }
    } finally {
      if (montadoRef.current) setProcesandoAccion(false);
    }
  };

  const abrirChatWhatsapp = () => {
    if (!usuario?.celular) {
      setError('Este usuario no tiene un número celular registrado.');
      return;
    }
    const numeroLimpio = String(usuario.celular).replace(/\D/g, '');
    if (numeroLimpio.length < 8) {
      setError('El formato del número celular no es válido.');
      return;
    }
    const url = `https://wa.me/${numeroLimpio}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const esUsuarioEliminadoOInactivo = (u) => {
    if (!u) return false;
    const s = String(u.subscriptionStatus || u.estado || '').toLowerCase().trim();
    return (
      u.eliminado === true ||
      u.isDeleted === true ||
      u.status === 'deleted' ||
      s === 'inactivo' ||
      s === 'eliminado'
    );
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '—';
    try {
      if (fecha.toDate) return fecha.toDate().toLocaleDateString('es-CL');
      return new Date(fecha).toLocaleDateString('es-CL');
    } catch {
      return '—';
    }
  };

  const mapValoresGenericos = {
    noPeroQuieroInformacionNutricion: 'No, pero quiero información nutricional',
    siTengoNutricionista: 'Sí, tengo nutricionista',
    noQuieroInformacionNutricion: 'No quiero información de nutrición',
    noTengoConocimiento: 'No tengo conocimiento',
    algoSé: 'Tengo algo de conocimiento',
    muchaExperiencia: 'Mucha experiencia',
    síPractico: 'Sí, practico deporte',
    noPractico: 'No, no practico deporte',
    si: 'Sí',
    no: 'No',
    interesarEntrenador: 'Interesarme con un entrenador',
    bajarDePeso: 'Bajar de peso',
    ganarMasaMuscular: 'Ganar masa muscular',
    tonificar: 'Tonificar',
    crecerTonificar: 'Crecer y Tonificar',
    aVeces: 'A veces',
    frecuentemente: 'Frecuentemente',
    raraVez: 'Rara vez',
    nunca: 'Nunca',
    principiante: 'Principiante',
    intermedio: 'Intermedio',
    avanzado: 'Avanzado'
  };

  const etiquetasPosturales = {
    hiperlordosis: 'Hiperlordosis',
    escoliosis: 'Escoliosis',
    cifosis: 'Cifosis',
    escapulasAladas: 'Escápulas aladas',
    toraxEscavado: 'Tórax escavado',
    otrosPosturales: 'Otros problemas posturales',
    descripcionOtrosPosturales: 'Detalle otros posturales',
    rodillasEnX: 'Rodillas en X',
    piePlano: 'Pie plano',
    pieCavo: 'Pie cavo',
    ptosisAbdominal: 'Ptosis abdominal',
    diferenciaExtremidades: 'Diferencia de extremidades'
  };

  const etiquetasComidas = {
    desayuno: 'Desayuno',
    almuerzo: 'Almuerzo',
    entreComidas: 'Entre comidas',
    cena: 'Cena',
    once: 'Once'
  };

  const mapValorComida = (valor) => {
    const traducciones = {
      noIngieroNada: 'No ingiero nada',
      alimentacionAdecuada: 'Alimentación adecuada',
      altoContenidoGrasaAzucarSal: 'Alto contenido de grasa/azúcar/sal',
      algoRapido: 'Algo rápido',
      dietaEspecial: 'Dieta especial'
    };
    return traducciones[valor] || valor;
  };

  const separarCamelCase = (texto) => {
    if (typeof texto !== 'string') return texto;
    const conEspacios = texto.replace(/([a-z])([A-Z])/g, '$1 $2');
    return conEspacios.charAt(0).toUpperCase() + conEspacios.slice(1);
  };

  const formatearValor = (clave, val) => {
    if (clave === 'problemasPosturales' && typeof val === 'object' && val !== null) {
      const items = Object.entries(val).filter(
        ([k, v]) => !(k === 'descripcionOtrosPosturales' && (!v || String(v).trim() === ''))
      );
      if (items.length === 0) return '—';
      return (
        <div style={styles.tagGrid}>
          {items.map(([k, v]) => {
            const esPositivo = typeof v === 'boolean' ? v : Boolean(v);
            return (
              <div
                key={k}
                style={{
                  ...styles.tagPill,
                  backgroundColor: esPositivo ? '#fdf2f8' : '#f8fafc',
                  border: esPositivo ? '1px solid #fbcfe8' : '1px solid #e2e8f0',
                  color: esPositivo ? APP_PRIMARY : '#64748b'
                }}
              >
                <span style={{ fontWeight: '600' }}>{etiquetasPosturales[k] || k}:</span>
                <span>{typeof v === 'boolean' ? (v ? 'Sí' : 'No') : String(v)}</span>
              </div>
            );
          })}
        </div>
      );
    }

    if (clave === 'comidas' && typeof val === 'object' && val !== null) {
      const comidasValidas = Object.entries(val).filter(([, v]) => v && String(v).trim() !== '');
      if (comidasValidas.length === 0) return '—';
      return (
        <div style={styles.mealsContainer}>
          {comidasValidas.map(([k, v]) => (
            <div key={k} style={styles.mealItem}>
              <span style={styles.mealLabel}>{etiquetasComidas[k] || k}</span>
              <span style={styles.mealValue}>{mapValorComida(v)}</span>
            </div>
          ))}
        </div>
      );
    }

    if (typeof val === 'string' && mapValoresGenericos[val]) {
      return mapValoresGenericos[val];
    }

    if (val === null || val === undefined || (typeof val === 'string' && val.trim() === '')) {
      return '—';
    }

    if (typeof val === 'string') {
      const texto = val.trim().toLowerCase();
      if (texto === 'si' || texto === 'sí') {
        return (
          <span style={styles.boolYes}>
            <FaCheckCircle size={12} /> Sí
          </span>
        );
      }
      if (texto === 'no') {
        return (
          <span style={styles.boolNo}>
            <FaTimesCircle size={12} /> No
          </span>
        );
      }
      return separarCamelCase(val);
    }

    if (typeof val === 'boolean') {
      return val ? (
        <span style={styles.boolYes}>
          <FaCheckCircle size={12} /> Sí
        </span>
      ) : (
        <span style={styles.boolNo}>
          <FaTimesCircle size={12} /> No
        </span>
      );
    }

    return String(val);
  };

  const secciones = [
    {
      titulo: 'Datos Personales y Contacto',
      icono: <FaUser color={APP_PRIMARY} size={15} />,
      campos: [
        { clave: 'nombreCompleto', etiqueta: 'Nombre Completo' },
        { clave: 'nombre', etiqueta: 'Nombre' },
        { clave: 'apellido', etiqueta: 'Apellido' },
        { clave: 'email', etiqueta: 'Email' },
        { clave: 'rol', etiqueta: 'Rol' },
        { clave: 'celular', etiqueta: 'Celular' },
        { clave: 'edad', etiqueta: 'Edad' },
        { clave: 'actividadOcupacion', etiqueta: 'Actividad u Ocupación' }
      ]
    },
    {
      titulo: 'Medidas Antropométricas',
      icono: <FaWeight color="#16a34a" size={15} />,
      campos: [
        { clave: 'peso', etiqueta: 'Peso (kg)' },
        { clave: 'altura', etiqueta: 'Altura (cm)' },
        { clave: 'imc', etiqueta: 'IMC' },
        { clave: 'circunferenciaCintura', etiqueta: 'Circunferencia de Cintura (cm)' }
      ]
    },
    {
      titulo: 'Entrenamiento y Objetivos',
      icono: <FaRunning color="#2563eb" size={15} />,
      campos: [
        { clave: 'objetivoEntrenamiento', etiqueta: 'Objetivo Principal' },
        { clave: 'motivo', etiqueta: 'Motivo' },
        { clave: 'practicaDeporte', etiqueta: '¿Practica Deporte?' },
        { clave: 'frecuenciaDeporte', etiqueta: 'Frecuencia de Ejercicio' }
      ]
    },
    {
      titulo: 'Salud, Postura y Hábitos',
      icono: <FaHeartbeat color="#dc2626" size={15} />,
      campos: [
        { clave: 'horasSueno', etiqueta: 'Horas de Sueño al Día' },
        { clave: 'fuma', etiqueta: '¿Fuma?' },
        { clave: 'bebeAlcohol', etiqueta: '¿Consume Alcohol?' },
        { clave: 'tieneAlergia', etiqueta: '¿Tiene Alergias?' },
        { clave: 'detalleAlergia', etiqueta: 'Detalle de Alergias' },
        { clave: 'antecedentesMedicos', etiqueta: 'Antecedentes Médicos' },
        { clave: 'problemasPosturales', etiqueta: 'Evaluación Postural' }
      ]
    },
    {
      titulo: 'Nutrición y Comidas',
      icono: <FaUtensils color="#d97706" size={15} />,
      campos: [
        { clave: 'nutricionista', etiqueta: '¿Tiene Nutricionista?' },
        { clave: 'interesNutricion', etiqueta: 'Interés en Nutrición' },
        { clave: 'comidas', etiqueta: 'Distribución de Comidas Diarias' }
      ]
    }
  ];

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Cabecera con botón de regreso */}
        <div style={styles.headerRow}>
          <button onClick={() => navigate('/admin/usuarios')} style={styles.backButton}>
            <FaArrowLeft size={13} /> Volver a Usuarios
          </button>
          <span style={styles.headerBadge}>Ficha del Alumno</span>
        </div>

        {error && <div style={styles.errorBanner}>{error}</div>}
        {mensajeExito && <div style={styles.successBanner}>{mensajeExito}</div>}

        {cargando ? (
          <div style={styles.centerBox}>
            <p style={styles.statusText}>Cargando ficha del alumno de forma segura…</p>
          </div>
        ) : (
          usuario && (
            <>
              {/* Tarjeta de Resumen */}
              <div style={styles.heroCard}>
                <div style={styles.avatarCircle}>
                  <FaUser size={30} color={APP_PRIMARY} />
                </div>
                <div style={styles.heroInfo}>
                  <h1 style={styles.heroName}>
                    {usuario.nombreCompleto || `${usuario.nombre || ''} ${usuario.apellido || ''}`.trim() || 'Alumno'}
                  </h1>
                  <p style={styles.heroEmail}>{usuario.email}</p>
                  <div style={styles.heroBadges}>
                    <span style={styles.pillBadge}>Rol: {usuario.rol || 'usuario'}</span>
                    {usuario.plan && <span style={styles.pillBadgeActive}>Plan: {usuario.plan}</span>}
                    {esUsuarioEliminadoOInactivo(usuario) ? (
                      <span style={styles.badgeDeleted}>Inactivo / Baja</span>
                    ) : (usuario.subscriptionStatus === 'active' || usuario.subscriptionStatus === 'activo') ? (
                      <span style={styles.badgeActive}>Activo</span>
                    ) : (
                      <span style={styles.badgePending}>Estado: {usuario.subscriptionStatus || 'Pendiente'}</span>
                    )}
                  </div>
                </div>

                {/* Botones de acción rápida */}
                <div style={styles.actionButtonsContainer}>
                  {usuario.celular && (
                    <button
                      type="button"
                      onClick={abrirChatWhatsapp}
                      style={styles.btnWhatsapp}
                      title="Abrir WhatsApp"
                    >
                      <FaWhatsapp size={14} /> WhatsApp
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={activarUsuario}
                    disabled={procesandoAccion}
                    style={{
                      ...styles.btnActivar,
                      opacity: procesandoAccion ? 0.7 : 1,
                      cursor: procesandoAccion ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <FaUserCheck size={14} /> {procesandoAccion ? 'Guardando…' : 'Activar / Renovar'}
                  </button>

                  {!esUsuarioEliminadoOInactivo(usuario) && (
                    <button
                      type="button"
                      onClick={() => setMostrarModalBaja(true)}
                      disabled={procesandoAccion}
                      style={{
                        ...styles.btnBaja,
                        opacity: procesandoAccion ? 0.7 : 1,
                        cursor: procesandoAccion ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <FaUserSlash size={14} /> Dar de baja
                    </button>
                  )}
                </div>
              </div>

              {/* Panel de Estado de Suscripción */}
              <div style={styles.subscriptionCard}>
                <div style={styles.subHeader}>
                  <FaCalendarAlt color={APP_PRIMARY} size={16} />
                  <span style={styles.subTitle}>Información de Suscripción</span>
                </div>
                <div style={styles.subGrid}>
                  <div>
                    <span style={styles.subLabel}>Inicio del plan</span>
                    <strong style={styles.subValue}>{formatearFecha(usuario.planStart)}</strong>
                  </div>
                  <div>
                    <span style={styles.subLabel}>Vencimiento</span>
                    <strong style={styles.subValue}>{formatearFecha(usuario.planEnd)}</strong>
                  </div>
                  <div>
                    <span style={styles.subLabel}>¿Usó Plan de Prueba (7 días)?</span>
                    <strong style={styles.subValue}>
                      {usuario.haUsadoTrial || usuario.trialUtilizado ? 'Sí (Bloqueado)' : 'No (Disponible)'}
                    </strong>
                  </div>
                  <div>
                    <span style={styles.subLabel}>Accesos al sistema</span>
                    <strong style={styles.subValue}>{usuario.accesos ?? 0} veces</strong>
                  </div>
                </div>
              </div>

              {/* Secciones de datos */}
              <div style={styles.sectionsGrid}>
                {secciones.map((sec) => {
                  const camposValidos = sec.campos.filter((c) => c.clave in usuario);
                  if (camposValidos.length === 0) return null;

                  return (
                    <div key={sec.titulo} style={styles.sectionCard}>
                      <div style={styles.sectionHeader}>
                        <div style={styles.iconBox}>{sec.icono}</div>
                        <h2 style={styles.sectionTitle}>{sec.titulo}</h2>
                      </div>
                      <div style={styles.dataRowsContainer}>
                        {camposValidos.map(({ clave, etiqueta }) => (
                          <div key={clave} style={styles.dataRow}>
                            <span style={styles.labelCol}>{etiqueta}</span>
                            <div style={styles.valueCol}>{formatearValor(clave, usuario[clave])}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Historial de Fichas */}
              <div style={styles.historialContainer}>
                <HistorialFichasAdmin uid={uid} />
              </div>
            </>
          )
        )}
      </div>

      {/* Modal Reactivo para Confirmar Baja */}
      {mostrarModalBaja && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <h3 style={{ margin: '0 0 10px 0', color: '#1e293b' }}>¿Dar de baja al alumno?</h3>
            <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 20px 0', lineHeight: 1.5 }}>
              El alumno quedará registrado como <b>Inactivo / Baja</b>. Si intenta volver a iniciar sesión, será redirigido a la pantalla de planes.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
              <button
                type="button"
                onClick={() => setMostrarModalBaja(false)}
                disabled={procesandoAccion}
                style={styles.btnModalCancelar}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={ejecutarDarDeBaja}
                disabled={procesandoAccion}
                style={styles.btnModalConfirmar}
              >
                {procesandoAccion ? 'Procesando…' : 'Sí, dar de baja'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barra flotante inferior */}
      <div style={navDockStyles.dockContainer}>
        <div style={navDockStyles.dockPill}>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            style={navDockStyles.dockButton}
          >
            <div style={navDockStyles.iconWrapper}>
              <FaHome size={14} />
            </div>
            <span>Inicio</span>
          </button>

          <div style={navDockStyles.divider} />

          <button
            type="button"
            onClick={handleLogout}
            style={{ ...navDockStyles.dockButton, color: '#dc2626' }}
          >
            <div
              style={{
                ...navDockStyles.iconWrapper,
                backgroundColor: '#fee2e2',
                color: '#dc2626'
              }}
            >
              <FaSignOutAlt size={13} />
            </div>
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================== ESTILOS =========================== */
const styles = {
  page: {
    background: '#f8fafc',
    minHeight: '100vh',
    padding: '24px 16px 120px 16px',
    boxSizing: 'border-box',
    fontFamily: "'Poppins', sans-serif"
  },
  container: {
    maxWidth: 960,
    margin: '0 auto'
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  },
  backButton: {
    background: '#fdf2f8',
    color: APP_PRIMARY,
    border: '1px solid #fbcfe8',
    padding: '8px 16px',
    borderRadius: 50,
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: 13,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    transition: 'all 0.2s ease'
  },
  headerBadge: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: APP_PRIMARY,
    background: '#fdf2f8',
    padding: '4px 12px',
    borderRadius: 20
  },
  errorBanner: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    padding: '12px 16px',
    borderRadius: 12,
    fontSize: 13,
    marginBottom: 20,
    fontWeight: '500'
  },
  successBanner: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    color: '#15803d',
    padding: '12px 16px',
    borderRadius: 12,
    fontSize: 13,
    marginBottom: 20,
    fontWeight: '600'
  },
  centerBox: {
    background: '#ffffff',
    padding: '40px 20px',
    borderRadius: 22,
    textAlign: 'center',
    border: '1px solid #f1f5f9',
    boxShadow: '0 8px 30px rgba(0,0,0,0.03)'
  },
  statusText: {
    margin: 0,
    fontSize: 15,
    color: '#64748b'
  },
  heroCard: {
    background: '#ffffff',
    borderRadius: 22,
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
    border: '1px solid #f1f5f9',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.04)',
    marginBottom: 16,
    flexWrap: 'wrap'
  },
  avatarCircle: {
    width: 68,
    height: 68,
    borderRadius: '50%',
    background: '#fdf2f8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid #fbcfe8',
    flexShrink: 0
  },
  heroInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: '1 1 240px'
  },
  heroName: {
    fontSize: 22,
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
    letterSpacing: '-0.3px'
  },
  heroEmail: {
    fontSize: 13,
    color: '#64748b',
    margin: 0
  },
  heroBadges: {
    display: 'flex',
    gap: 8,
    marginTop: 6,
    flexWrap: 'wrap'
  },
  pillBadge: {
    fontSize: 11,
    fontWeight: '600',
    padding: '3px 10px',
    borderRadius: 50,
    background: '#f1f5f9',
    color: '#475569'
  },
  pillBadgeActive: {
    fontSize: 11,
    fontWeight: '600',
    padding: '3px 10px',
    borderRadius: 50,
    background: '#fdf2f8',
    color: APP_PRIMARY,
    border: '1px solid #fbcfe8',
    textTransform: 'capitalize'
  },
  badgeActive: {
    fontSize: 11,
    fontWeight: '600',
    padding: '3px 10px',
    borderRadius: 50,
    background: '#f0fdf4',
    color: '#16a34a',
    border: '1px solid #bbf7d0'
  },
  badgePending: {
    fontSize: 11,
    fontWeight: '600',
    padding: '3px 10px',
    borderRadius: 50,
    background: '#fffbeb',
    color: '#b45309',
    border: '1px solid #fde68a'
  },
  badgeDeleted: {
    fontSize: 11,
    fontWeight: '600',
    padding: '3px 10px',
    borderRadius: 50,
    background: '#f1f5f9',
    color: '#475569',
    border: '1px solid #cbd5e1'
  },
  actionButtonsContainer: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap'
  },
  btnWhatsapp: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 50,
    border: 'none',
    backgroundColor: '#25D366',
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
    cursor: 'pointer'
  },
  btnActivar: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 50,
    border: 'none',
    backgroundColor: '#16a34a',
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
    cursor: 'pointer'
  },
  btnBaja: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 50,
    border: 'none',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    fontSize: 12,
    fontWeight: '600',
    cursor: 'pointer'
  },
  subscriptionCard: {
    background: '#ffffff',
    borderRadius: 18,
    padding: '20px',
    border: '1px solid #f1f5f9',
    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.02)',
    marginBottom: 20
  },
  subHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 14,
    borderBottom: '1px solid #f1f5f9',
    paddingBottom: 8
  },
  subTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1e293b'
  },
  subGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 16
  },
  subLabel: {
    display: 'block',
    fontSize: 11,
    color: '#64748b',
    marginBottom: 2
  },
  subValue: {
    fontSize: 14,
    color: '#1e293b'
  },
  sectionsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  },
  sectionCard: {
    background: '#ffffff',
    borderRadius: 20,
    padding: '22px 24px',
    border: '1px solid #f1f5f9',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
    borderBottom: '1px solid #f1f5f9',
    paddingBottom: 12
  },
  iconBox: {
    width: 32,
    height: 32,
    borderRadius: 10,
    background: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  dataRowsContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  dataRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '10px 0',
    borderBottom: '1px solid #f8fafc',
    gap: 16,
    flexWrap: 'wrap'
  },
  labelCol: {
    fontSize: 13,
    fontWeight: '600',
    color: '#64748b',
    minWidth: 200,
    flex: '1 1 200px'
  },
  valueCol: {
    fontSize: 13,
    color: '#1e293b',
    flex: '2 1 260px',
    textAlign: 'left'
  },
  boolYes: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: '#16a34a',
    fontWeight: '600'
  },
  boolNo: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: '#94a3b8',
    fontWeight: '500'
  },
  tagGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8
  },
  tagPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 8,
    fontSize: 12
  },
  mealsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%'
  },
  mealItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 12px',
    background: '#f8fafc',
    borderRadius: 8,
    border: '1px solid #f1f5f9',
    fontSize: 12
  },
  mealLabel: {
    fontWeight: '600',
    color: '#475569'
  },
  mealValue: {
    color: '#1e293b'
  },
  historialContainer: {
    marginTop: 20
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(15, 23, 42, 0.4)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: 16
  },
  modalCard: {
    background: '#ffffff',
    padding: 28,
    borderRadius: 20,
    width: '100%',
    maxWidth: 380,
    textAlign: 'center',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    border: '1px solid #f1f5f9'
  },
  btnModalCancelar: {
    padding: '10px 18px',
    border: '1px solid #e2e8f0',
    background: '#ffffff',
    color: '#475569',
    borderRadius: 50,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: '600'
  },
  btnModalConfirmar: {
    padding: '10px 18px',
    border: 'none',
    background: '#dc2626',
    color: '#ffffff',
    borderRadius: 50,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: '600',
    boxShadow: '0 3px 10px rgba(220, 38, 38, 0.25)'
  }
};

const navDockStyles = {
  dockContainer: {
    position: 'fixed',
    bottom: 20,
    left: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    pointerEvents: 'none',
    padding: '0 16px',
    boxSizing: 'border-box'
  },
  dockPill: {
    pointerEvents: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.94)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '1.5px solid rgba(241, 245, 249, 0.95)',
    borderRadius: 50,
    padding: '6px 12px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease'
  },
  dockButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: 'none',
    borderRadius: 50,
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: "'Poppins', sans-serif",
    transition: 'all 0.2s ease',
    outline: 'none',
    backgroundColor: 'transparent',
    color: '#64748b',
    fontWeight: '600'
  },
  iconWrapper: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    backgroundColor: '#f1f5f9',
    color: '#64748b'
  },
  divider: {
    width: 1,
    height: 22,
    backgroundColor: '#e2e8f0',
    margin: '0 2px'
  }
};